/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/interactive-supports-focus */
import type {
	ComponentType,
	CSSProperties,
	FC,
	ReactElement,
	ReactNode,
	SVGProps,
} from "react"
import type { ExternalToast } from "sonner"
import { memo, useCallback, useEffect, useRef } from "react"
import { motion } from "motion/react"
import {
	IconCircleWarningOutline18 as AlertCircle,
	IconCircleInfoOutline18 as Info,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"
import { toast as sonnerToast, Toaster as SonnerToaster } from "sonner"

import { cn } from "@productlane/lib/src/cn"
import {
	Button,
	IconButton,
	IconWithHalo,
	IssueCompletedIcon,
	useToastPump,
} from "@productlane/ui"

import {
	MESSAGE_UNDO_CANCELLATION_TIMEOUT,
	useUndoContext,
} from "@/context/undo-context"

type ToastIconComponent = ComponentType<{ className?: string }>

type ToastId = string | number

type UndoHandler = () => void | Promise<void>

let pendingUndo: { toastId: ToastId; run: UndoHandler } | null = null

function setPendingUndo(toastId: ToastId, run: UndoHandler) {
	pendingUndo = { toastId, run }
}

function clearPendingUndo(toastId: ToastId) {
	if (pendingUndo?.toastId === toastId) {
		pendingUndo = null
	}
}

type ToastType = "error" | "primary" | "success"
type ToastLayoutType = "default" | "inline"

interface ToastProps {
	action?: {
		callback: () => unknown
		label: string
		shortcut?: ReactNode
		Icon?: LucideIcon
		className?: string
	}
	descriptionAction?: {
		callback: () => unknown
		label?: string
	}
	layout?: ToastLayoutType
	description?: string | ReactNode
	icon?: ToastIconComponent
	iconVariant?: "error" | "primary" | "success" | "warning"
	showProgress?: boolean
	id: ToastId
	duration?: number
	pumpCount?: number
	title: string
	type: ToastType
	onUndo?: UndoHandler
}

interface UndoToastProps extends ToastProps {
	data: Record<string, unknown>
}

const SUCCESS_DURATION = 3000
const ERROR_DURATION = 5000
const INFO_DURATION = 5000

const ToastIcon: Record<ToastType, ToastIconComponent> = {
	error: AlertCircle,
	primary: Info,
	success: IssueCompletedIcon,
}

export const Toast = memo(
	({
		title,
		description,
		type,
		id,
		duration = INFO_DURATION,
		action,
		icon,
		iconVariant,
		descriptionAction,
		layout = "default",
		showProgress = false,
		onUndo,
		pumpCount = 0,
	}: ToastProps) => {
		const Icon = icon ?? ToastIcon[type]
		const cardRef = useRef<HTMLDivElement>(null)
		useToastPump(cardRef, pumpCount)

		const onClick = useCallback(() => {
			action?.callback()
			toast.dismiss(id)
		}, [action, id])

		const descriptionOnClick = useCallback(() => {
			descriptionAction?.callback()
			toast.dismiss(id)
		}, [descriptionAction, id])

		const onUndoClick = useCallback(() => {
			if (!onUndo) return
			clearPendingUndo(id)
			void onUndo()
			toast.dismiss(id)
		}, [onUndo, id])

		useEffect(() => {
			if (!onUndo) return
			setPendingUndo(id, onUndo)
			return () => clearPendingUndo(id)
		}, [onUndo, id])

		const isUndoAction = action?.label === "Undo"
		const undoButton = onUndo ? (
			<Button onClick={onUndoClick} variant="outline" size="xs">
				Undo
				{action?.shortcut && (
					<span className="ml-1 flex items-center">{action.shortcut}</span>
				)}
			</Button>
		) : isUndoAction && action ? (
			<Button
				onClick={onClick}
				variant="outline"
				size="xs"
				Icon={action.Icon}
				className={action.className}
			>
				{action.label}
				{action.shortcut && (
					<span className="ml-1 flex items-center">{action.shortcut}</span>
				)}
			</Button>
		) : null

		return (
			<div
				ref={cardRef}
				className="relative ml-auto h-full w-full max-w-[330px] overflow-hidden rounded-xl bg-white px-2 py-3 shadow-lg shadow-black/[0.1] ring-1 ring-black/[0.07] dark:bg-gray-850/90 dark:ring-white/10 dark:backdrop-blur-sm"
			>
				{duration !== Infinity && showProgress && (
					<div className="absolute bottom-0 left-0 right-0 h-1 overflow-hidden bg-gray-200 dark:bg-gray-700">
						<motion.div
							className="h-full bg-green-500"
							initial={{ scaleX: 1 }}
							animate={{ scaleX: 0 }}
							transition={{
								duration: duration / 1000,
								ease: "linear",
							}}
							style={{ originX: 0 }}
						/>
					</div>
				)}
				<div
					className={cn(
						"mr-2 grid grid-cols-[auto,1fr] grid-rows-[auto,auto] gap-x-2",
						{
							"grid-cols-[auto,minmax(0,1fr),auto] items-center":
								layout === "inline",
						},
					)}
				>
					<div
						className={cn("self-start", {
							"self-center":
								layout === "inline" && !description && !descriptionAction,
						})}
					>
						<IconWithHalo
							variant={iconVariant ?? type}
							icon={<Icon className="h-4 w-4" />}
						/>
					</div>
					<div
						className={cn(
							"flex items-center gap-1.5",
							layout === "inline"
								? "col-start-3 row-start-1 self-center"
								: {
										"absolute right-2": true,
										"top-3": description || descriptionAction,
										"top-1/2 -translate-y-1/2":
											!description && !descriptionAction,
									},
						)}
					>
						{undoButton}
						<IconButton
							onClick={() => {
								toast.dismiss(id)
							}}
							size="sm"
							Icon={XIcon}
							variant="ghost"
							className="hover:bg-transparent dark:hover:bg-transparent"
						/>
					</div>
					<div className="col-start-2 row-start-1 min-w-0 self-center">
						<div
							className={cn(
								"flex select-none flex-row items-center justify-between",
								{ "pr-28": undoButton && layout !== "inline" },
							)}
						>
							<div
								className={cn("mt-0.5 text-sm font-semibold", {
									"mb-1": description || descriptionAction,
									"mt-0": !description && !descriptionAction,
								})}
							>
								{title}
							</div>
						</div>

						<div className="inline text-sm text-secondary">
							{descriptionAction && (
								<span
									role="button"
									onClick={descriptionOnClick}
									className="-mx-1 inline select-none break-words rounded-md px-1 leading-[1.35] hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-gray-800 dark:hover:text-gray-100"
									style={{
										boxDecorationBreak: "clone",
										WebkitBoxDecorationBreak: "clone",
										paddingBlock: "0.45em",
										marginBlock: "-0.15em",
									}}
								>
									{descriptionAction.label}
								</span>
							)}

							{description && (
								<span
									className={cn({
										"ml-1": descriptionAction,
									})}
								>
									{description}
								</span>
							)}
						</div>

						{layout === "default" && action && !isUndoAction && (
							<div className="-mx-1 flex items-center gap-2">
								<Button
									onClick={onClick}
									variant="ghost"
									size="sm"
									Icon={action.Icon}
									className={cn(
										"-ml-1 text-secondary underline hover:bg-transparent hover:text-primary",
										action.className,
									)}
								>
									{action.label}
									{action.shortcut && (
										<span className="ml-1 flex items-center">
											{action.shortcut}
										</span>
									)}
								</Button>
							</div>
						)}
					</div>
					{layout === "inline" && action && !isUndoAction && (
						<div className="-mx-1 flex items-center gap-2">
							<Button
								onClick={onClick}
								variant="outline"
								size="sm"
								Icon={action.Icon}
								className={action.className}
							>
								{action.label}
								{action.shortcut && (
									<span className="ml-1 flex items-center">
										{action.shortcut}
									</span>
								)}
							</Button>
						</div>
					)}
				</div>
			</div>
		)
	},
)

const EmailUndo: FC<UndoToastProps> = memo((props) => {
	const { isMessageIncluded } = useUndoContext()
	const { id, data } = props

	const isPending =
		typeof data?.messageId === "string" && isMessageIncluded(data.messageId)

	useEffect(() => {
		if (!isPending) toast.dismiss(id)
	}, [isPending, id])

	return <Toast {...props} />
})

type ShowToastProps<T = ToastProps> = Omit<
	T,
	"id" | "duration" | "type" | "pumpCount"
> & {
	id?: string
	time?: number
}

type ToastBase = Required<Pick<ToastProps, "id" | "duration" | "pumpCount">>

type ToastRender = (base: ToastBase) => ReactElement

function generateToastId() {
	return `t-${Math.random().toString(36).slice(2, 11)}-${Date.now()}`
}

// Present only while a toast is on screen, so it doubles as the "shown" check.
const pumpCounts = new Map<ToastId, number>()
const toastIdsByContent = new Map<string, string>()

// A toast with an undo never merges into another, so every undo stays reachable.
function contentKey(type: ToastType, props: ShowToastProps) {
	if (props.onUndo || props.action?.label === "Undo") return undefined
	if (props.description !== undefined && typeof props.description !== "string")
		return undefined
	return JSON.stringify([
		type,
		props.title,
		props.description,
		props.descriptionAction?.label,
		props.action?.label,
	])
}

function resolveToastId(explicitId: string | undefined, key?: string) {
	const id =
		explicitId ??
		(key ? toastIdsByContent.get(key) : undefined) ??
		generateToastId()
	if (key) toastIdsByContent.set(key, id)
	return id
}

function forgetToast(id: ToastId) {
	pumpCounts.delete(id)
	clearPendingUndo(id)
	for (const [key, value] of toastIdsByContent) {
		if (value === id) toastIdsByContent.delete(key)
	}
}

function renderToast(
	id: string,
	render: ToastRender,
	duration: number,
	{ pump = true }: { pump?: boolean } = {},
) {
	const shownCount = pumpCounts.get(id)
	const pumpCount = shownCount === undefined ? 0 : shownCount + (pump ? 1 : 0)
	pumpCounts.set(id, pumpCount)
	const options: ExternalToast = {
		id,
		duration,
		onDismiss: () => forgetToast(id),
		onAutoClose: () => forgetToast(id),
	}
	sonnerToast.custom(
		(toastId) => render({ id: toastId, duration, pumpCount }),
		options,
	)
	return id
}

function dismissToast(id?: ToastId) {
	if (id !== undefined) {
		forgetToast(id)
		sonnerToast.dismiss(id)
	} else {
		pumpCounts.clear()
		toastIdsByContent.clear()
		pendingUndo = null
		sonnerToast.dismiss()
	}
}

function showToast(
	type: ToastType,
	props: ShowToastProps,
	defaultDuration: number,
) {
	const { id, time, ...rest } = props
	return renderToast(
		resolveToastId(id, contentKey(type, rest)),
		(base) => <Toast {...rest} {...base} type={type} />,
		time ?? defaultDuration,
	)
}

export const toast = {
	success: (props: ShowToastProps) =>
		showToast("success", props, SUCCESS_DURATION),
	error: (props: ShowToastProps) => showToast("error", props, ERROR_DURATION),
	info: (props: ShowToastProps) => showToast("primary", props, INFO_DURATION),
	undo: (props: ShowToastProps<UndoToastProps>) => {
		const { id, time: _time, ...rest } = props
		return renderToast(
			id ?? generateToastId(),
			(base) => <EmailUndo {...rest} {...base} type="primary" />,
			MESSAGE_UNDO_CANCELLATION_TIMEOUT,
		)
	},
	loading: ({ time: _time, ...props }: ShowToastProps) =>
		showToast("primary", props, Infinity),
	promise: <T,>(
		promise: Promise<T>,
		msgs: { loading: string; success: string; error: string },
		id?: string,
	) => {
		const toastId = id ?? generateToastId()
		renderToast(
			toastId,
			(base) => <Toast {...base} type="primary" title={msgs.loading} />,
			Infinity,
		)

		promise
			.then((p) => {
				renderToast(
					toastId,
					(base) => <Toast {...base} title={msgs.success} type="success" />,
					SUCCESS_DURATION,
					{ pump: false },
				)
				return p
			})
			.catch(() => {
				renderToast(
					toastId,
					(base) => <Toast {...base} title={msgs.error} type="error" />,
					ERROR_DURATION,
					{ pump: false },
				)
			})

		return promise
	},
	dismiss: dismissToast,
}

function useGlobalUndoShortcut() {
	useEffect(() => {
		const handler = (event: KeyboardEvent) => {
			if (!pendingUndo) return
			const isUndoCombo =
				(event.metaKey || event.ctrlKey) &&
				!event.shiftKey &&
				!event.altKey &&
				event.key.toLowerCase() === "z"
			if (!isUndoCombo) return
			event.preventDefault()
			event.stopPropagation()
			event.stopImmediatePropagation()
			const { toastId, run } = pendingUndo
			pendingUndo = null
			void run()
			dismissToast(toastId)
		}
		window.addEventListener("keydown", handler, { capture: true })
		return () =>
			window.removeEventListener("keydown", handler, { capture: true })
	}, [])
}

const TOASTER_STYLE = {
	"--width": "330px",
	fontFamily: "inherit",
} as CSSProperties

// Sonner only sizes styled toasts, so a custom toast's li would shrink to its content.
const TOAST_OPTIONS = { unstyled: true, className: "w-[var(--width)]" }

const IMPERSONATION_BAR_HEIGHT = "var(--impersonation-bar-height, 0px)"

export const Toaster = memo(() => {
	useGlobalUndoShortcut()
	return (
		<SonnerToaster
			position="bottom-right"
			// Linear's list: every toast stays expanded instead of collapsing into a deck.
			expand
			visibleToasts={3}
			gap={8}
			offset={{ right: 16, bottom: `calc(20px + ${IMPERSONATION_BAR_HEIGHT})` }}
			mobileOffset={{ bottom: `calc(16px + ${IMPERSONATION_BAR_HEIGHT})` }}
			style={TOASTER_STYLE}
			toastOptions={TOAST_OPTIONS}
		/>
	)
})

type LucideIcon = FC<SVGProps<SVGSVGElement>>
